/**
 * 風格渲染 API
 *
 * 提供座位規劃圖的風格渲染功能
 */

import { CONFIG } from '@/config';

// ========================================================================
// 型別定義
// ========================================================================

/** 風格渲染請求參數 */
export interface StyleRenderRequest {
  /** 規劃圖 Blob */
  image: Blob;
  /** 風格名稱（選擇性） */
  style?: string;
  /** 自訂完整提示詞（選擇性） */
  customPrompt?: string;
  /** Temperature 參數 */
  temperature?: number;
}

/** 風格渲染回應 */
export interface StyleRenderResponse {
  /** 渲染後的圖片 Blob */
  imageBlob: Blob;
  /** 圖片 URL */
  imageUrl: string;
}

// ========================================================================
// 風格翻譯映射表
// ========================================================================

const TRANSLATION_MAP: Record<string, string> = {
  // Renderer Styles - 精選座位規劃適用風格
  '現代簡約': 'Modern Minimalist',
  '溫暖木質': 'Warm Wooden',
  '清水模': 'Architectural Concrete',
  '北歐風': 'Nordic Scandinavian',
  '日式禪風': 'Japanese Zen',
  '工業風': 'Industrial Loft',
  '科技感': 'Futuristic Sci-fi',
  '極簡白': 'Minimalist White',
  '法式古典': 'French Classical',
  '新中式': 'New Chinese Style',
  '美式經典': 'American Classic',
  '輕奢風': 'Light Luxury',
  '地中海': 'Mediterranean',
  '中世紀現代': 'Mid-Century Modern',
  '義式極簡': 'Italian Minimalism',
  '莫蘭迪色': 'Morandi Colors',
  '當代風格': 'Contemporary',
  '日式北歐': 'Japandi',
  '有機現代': 'Organic Modern',
  '折衷主義': 'Eclectic',
};

/**
 * 翻譯風格名稱（中文 -> 英文）
 */
export function translateStyle(chineseStyle: string): string {
  return TRANSLATION_MAP[chineseStyle] || chineseStyle;
}

/**
 * 取得所有風格選項
 */
export function getStyleOptions(): string[] {
  return Object.keys(TRANSLATION_MAP);
}

// ========================================================================
// API 函數
// ========================================================================

/**
 * 呼叫後端風格渲染 API
 */
export async function renderStyleImage(request: StyleRenderRequest): Promise<StyleRenderResponse> {
  const formData = new FormData();
  formData.append('image', request.image, 'floorplan.png');

  // 添加風格參數（需要翻譯成英文）
  if (request.style) {
    const translatedStyle = translateStyle(request.style);
    formData.append('style', translatedStyle);
  }

  // 添加自訂提示詞
  if (request.customPrompt) {
    formData.append('custom_prompt', request.customPrompt);
  }

  // 使用固定 temperature 值
  const temperature = request.temperature ?? 1.0;
  formData.append('temperature', temperature.toString());

  // 調用後端 API
  const apiUrl = `${CONFIG.API_BASE_URL}/api/v1/render`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: '伺服器錯誤' }));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }

  // 接收圖片 Blob
  const imageBlob = await response.blob();
  const imageUrl = URL.createObjectURL(imageBlob);

  return {
    imageBlob,
    imageUrl,
  };
}
